import { useState, useRef, ChangeEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { 
  Upload, 
  Wand2, 
  ArrowLeft, 
  CreditCard,
  CheckCircle
} from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { useCredits } from '../context/CreditContext'
import { imageService } from '../services/images'
import LoadingSpinner from '../components/LoadingSpinner'

export default function GeneratePage() {
  const [selectedImage, setSelectedImage] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [generating, setGenerating] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')
  const { user, refreshUser } = useAuth()
  const { refreshTransactions } = useCredits()
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleImageSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('请选择图片文件')
      return
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('图片文件不能超过10MB')
      return
    }

    setSelectedImage(file)
    setError('')

    // Create preview
    const reader = new FileReader()
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string)
    }
    reader.readAsDataURL(file)
  }

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    const files = event.dataTransfer.files
    if (files.length > 0) {
      const file = files[0]
      if (file.type.startsWith('image/')) {
        setSelectedImage(file)
        const reader = new FileReader()
        reader.onload = (e) => {
          setImagePreview(e.target?.result as string)
        }
        reader.readAsDataURL(file)
      }
    }
  }

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
  }

  const generateImage = async () => {
    if (!selectedImage) {
      setError('请先选择一张图片')
      return
    }

    if (!user || user.credits < 1) {
      setError('积分不足，无法生成图片')
      return
    }

    setGenerating(true)
    setError('')

    try {
      // 后端会自动使用默认提示词，不需要前端传递
      const result = await imageService.generateImage(selectedImage)

      if (result.success && result.data) {
        console.log('Image generation successful, refreshing user data...')
        
        // Refresh both user credits and transactions
        await Promise.all([
          refreshUser(),
          refreshTransactions()
        ])
        
        console.log('User data refreshed successfully')
        setSuccess(true)
        setTimeout(() => {
          navigate('/dashboard')
        }, 2000)
      } else {
        setError(result.error || '生成图片失败')
      }
    } catch (err) {
      setError('生成图片失败，请重试')
    } finally {
      setGenerating(false)
    }
  }


  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">请登录后生成图片</p>
          <Link to="/login" className="btn-primary">
            立即登录
          </Link>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-sm border text-center max-w-md">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            生成成功！
          </h2>
          <p className="text-gray-600 mb-4">
            您的图片已成功生成，正在跳转到控制面板...
          </p>
          <LoadingSpinner size="md" className="mx-auto" />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <Link
                to="/dashboard"
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>返回控制面板</span>
              </Link>
              <div className="border-l border-gray-300 pl-4">
                <h1 className="text-xl font-semibold text-gray-900">AI 图片生成</h1>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 bg-blue-50 px-3 py-1 rounded-full">
                <CreditCard className="w-4 h-4 text-blue-600" />
                <span className="text-blue-600 font-medium">
                  {user.credits} 积分
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Credit Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
          <div className="flex items-center justify-center space-x-2 text-blue-700">
            <CreditCard className="w-5 h-5" />
            <span className="font-medium">
              您的积分: {user.credits} | 每张图片费用: 1 积分
            </span>
          </div>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Upload Section */}
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                选择图片
              </h2>
              
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors cursor-pointer"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => fileInputRef.current?.click()}
              >
                {imagePreview ? (
                  <div className="space-y-4">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="max-h-48 mx-auto rounded-lg shadow-md"
                    />
                    <p className="text-sm text-gray-600">
                      点击或拖拽新图片替换
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <Upload className="w-12 h-12 text-gray-400 mx-auto" />
                    <div>
                      <p className="text-lg text-gray-900">选择或拖拽图片到这里</p>
                      <p className="text-sm text-gray-500">
                        支持 PNG, JPG, WEBP 格式，最大 10MB
                      </p>
                    </div>
                  </div>
                )}
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
              </div>

              {selectedImage && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-700 flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4" />
                    <span>已选择: {selectedImage.name}</span>
                  </p>
                </div>
              )}
            </div>


            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            <button
              onClick={generateImage}
              disabled={generating || !selectedImage || user.credits < 1}
              className="w-full flex justify-center items-center space-x-2 btn-primary py-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {generating ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span>生成中...</span>
                </>
              ) : (
                <>
                  <Wand2 className="w-5 h-5" />
                  <span>生成图片 (1 积分)</span>
                </>
              )}
            </button>

            {user.credits < 1 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <CreditCard className="w-5 h-5 text-yellow-600" />
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-yellow-800">
                      积分不足
                    </h3>
                    <p className="text-sm text-yellow-700">
                      您至少需要1个积分才能生成图片
                    </p>
                  </div>
                  <Link to="/credits" className="btn-primary text-sm">
                    购买积分
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}